import { useEffect } from "react";

/**
 * Lightweight SEO meta tag manager — sets <title>, meta description,
 * canonical, OG tags, and Twitter card tags + a JSON-LD <script> for schema.org.
 * Cleans up on unmount (useful for SPA navigation).
 */
function upsertTag(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split("[")[0]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    el.setAttribute(k, v);
  });
  return el;
}

function setMeta(name, content) {
  if (!content) return null;
  return upsertTag(`meta[name="${name}"]`, { name, content });
}

function setProp(prop, content) {
  if (!content) return null;
  return upsertTag(`meta[property="${prop}"]`, { property: prop, content });
}

export default function useSEO({ title, description, canonical, ogImage, jsonLd, ogType = "website" }) {
  useEffect(() => {
    if (title) document.title = title;
    const tags = [];
    if (description) {
      tags.push(setMeta("description", description));
      tags.push(setProp("og:description", description));
      tags.push(setMeta("twitter:description", description));
    }
    if (title) {
      tags.push(setProp("og:title", title));
      tags.push(setMeta("twitter:title", title));
    }
    if (canonical) tags.push(upsertTag('link[rel="canonical"]', { rel: "canonical", href: canonical }));
    if (ogImage) {
      tags.push(setProp("og:image", ogImage));
      tags.push(setMeta("twitter:image", ogImage));
    }
    tags.push(setProp("og:type", ogType));
    tags.push(setMeta("twitter:card", "summary_large_image"));

    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.text = JSON.stringify(jsonLd);
      ldScript.dataset.seoHelper = "1";
      document.head.appendChild(ldScript);
    }

    return () => {
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd ? JSON.stringify(jsonLd) : ""]);
}
